import { useEffect, useRef } from 'react';
import { X, Download } from 'lucide-react';

import { Dialog, DialogContent } from '../../core/Dialog';
import { Badge } from '../../core/Badge';
import { formatBytes, formatDateTime } from '../../../utils/format';
import type { MediaAsset } from '../../../hooks/useMediaLibrary';

interface MediaViewerProps {
  asset: MediaAsset | null;
  onClose: () => void;
}

export function MediaViewer({ asset, onClose }: Readonly<MediaViewerProps>) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Pause video when the dialog closes.
  useEffect(() => {
    if (!asset && videoRef.current) {
      videoRef.current.pause();
    }
  }, [asset]);

  if (!asset) return null;

  const isImage = asset.type === 'image';

  const meta: { label: string; value: string }[] = [
    { label: 'File', value: asset.name },
    { label: 'Size', value: formatBytes(asset.sizeBytes) },
    { label: 'Type', value: asset.mimeType },
    { label: 'Uploaded', value: formatDateTime(asset.createdAt) },
    ...(asset.width && asset.height
      ? [{ label: 'Dimensions', value: `${asset.width} × ${asset.height} px` }]
      : []),
    ...(asset.durationMs != null
      ? [{ label: 'Duration', value: `${(asset.durationMs / 1000).toFixed(1)} s` }]
      : []),
    ...(asset.ownerEmail ? [{ label: 'Owner', value: asset.ownerEmail }] : []),
  ];

  return (
    <Dialog
      open={!!asset}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-5xl w-full p-0 overflow-hidden" hideClose>
        <div className="flex flex-col lg:flex-row h-full max-h-[90vh]">
          {/* Media */}
          <div className="relative flex-1 bg-black flex items-center justify-center min-h-[300px] lg:min-h-[500px]">
            {isImage ? (
              <img
                src={asset.url}
                alt={asset.altText ?? asset.name}
                className="max-w-full max-h-[80vh] object-contain"
              />
            ) : (
              <video
                ref={videoRef}
                src={asset.url}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] w-full"
              />
            )}

            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors backdrop-blur-sm"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Sidebar metadata */}
          <aside className="w-full lg:w-72 flex-shrink-0 border-l border-slate-700 bg-slate-800 p-5 overflow-y-auto">
            <div className="flex items-center gap-2 mb-4">
              <p className="text-sm font-bold text-white truncate flex-1">{asset.name}</p>
              {asset.status === 'archived' && <Badge variant="warning">Archived</Badge>}
            </div>

            <dl className="space-y-3">
              {meta.map((row) => (
                <div key={row.label}>
                  <dt className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                    {row.label}
                  </dt>
                  <dd className="text-xs text-slate-300 mt-0.5 break-all">{row.value}</dd>
                </div>
              ))}
            </dl>

            {/* Download */}
            <a
              href={asset.url}
              download={asset.name}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 hover:bg-violet-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
